import React, { useCallback, useEffect, useState } from 'react';

export const BHRIGU_SCHOOL_SCOPE_KEY = 'bhrigu_school_scope';

export const BHRIGU_SCHOOL_OPTIONS = [
  { id: 'parashari', labelEn: 'Parashari', labelHi: 'पराशरी' },
  { id: 'kp', labelEn: 'KP', labelHi: 'KP' },
  { id: 'bnn', labelEn: 'BNN', labelHi: 'BNN' },
  { id: 'jaimini', labelEn: 'Jaimini', labelHi: 'जैमिनी' },
  { id: 'lal-kitab', labelEn: 'Lal Kitab', labelHi: 'लाल किताब' },
  { id: 'swara-vigyan', labelEn: 'Swara Vigyan', labelHi: 'स्वर विज्ञान' },
  { id: 'surya-siddhanta', labelEn: 'Surya Siddhanta', labelHi: 'सूर्य सिद्धांत' },
  { id: 'all', labelEn: 'All schools', labelHi: 'सभी परंपराएँ' },
];

const VALID_SCHOOL_IDS = new Set(BHRIGU_SCHOOL_OPTIONS.map((o) => o.id));

/**
 * @param {string | null | undefined} raw
 * @returns {string}
 */
export function normalizeBhriguSchoolScope(raw) {
  const s = String(raw || 'parashari').toLowerCase().trim();
  return VALID_SCHOOL_IDS.has(s) ? s : 'parashari';
}

/**
 * @returns {string}
 */
export function readBhriguSchoolScope() {
  if (typeof sessionStorage === 'undefined') return 'parashari';
  try {
    return normalizeBhriguSchoolScope(sessionStorage.getItem(BHRIGU_SCHOOL_SCOPE_KEY));
  } catch {
    return 'parashari';
  }
}

/**
 * @param {string} school
 */
export function writeBhriguSchoolScope(school) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(BHRIGU_SCHOOL_SCOPE_KEY, normalizeBhriguSchoolScope(school));
  } catch {
    /* ignore */
  }
}

/**
 * @param {{
 *   showEnglish: boolean;
 *   isDark: boolean;
 *   value: string;
 *   onChange: (school: string) => void;
 *   disabled?: boolean;
 *   compact?: boolean;
 * }} props
 */
export function BhriguSchoolScopeChips({
  showEnglish,
  isDark,
  value,
  onChange,
  disabled = false,
  compact = false,
}) {
  const selected = normalizeBhriguSchoolScope(value);

  const handleSelect = useCallback(
    (school) => {
      if (disabled || school === selected) return;
      writeBhriguSchoolScope(school);
      onChange(school);
    },
    [disabled, onChange, selected],
  );

  return (
    <div
      className={`flex flex-wrap gap-1.5 ${compact ? '' : 'mb-3'}`}
      role="radiogroup"
      aria-label={showEnglish ? 'Astrology school scope' : 'ज्योतिष परंपरा चुनें'}
    >
      {BHRIGU_SCHOOL_OPTIONS.map((option) => {
        const active = selected === option.id;
        const label = showEnglish ? option.labelEn : option.labelHi;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => handleSelect(option.id)}
            className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold transition-colors disabled:opacity-50 sm:px-3 sm:py-1 sm:text-xs ${
              active
                ? isDark
                  ? 'border-amber-300/60 bg-amber-500/20 text-amber-100'
                  : 'border-amber-400 bg-amber-100 text-amber-950'
                : isDark
                  ? 'border-white/15 bg-white/5 text-violet-100 hover:bg-white/10'
                  : 'border-violet-200 bg-white text-violet-800 hover:bg-violet-50'
            } ${showEnglish ? '' : 'font-devanagari'}`}
            lang={showEnglish ? 'en' : 'hi'}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Hook: persisted school scope with referrer pre-selection.
 * @param {{ referrerSchool?: string | null; enabled?: boolean }} [opts]
 */
export function useBhriguSchoolScope(opts = {}) {
  const { referrerSchool = null, enabled = true } = opts;
  const [school, setSchool] = useState(() => readBhriguSchoolScope());

  useEffect(() => {
    if (!enabled) return;
    if (referrerSchool) {
      const next = normalizeBhriguSchoolScope(referrerSchool);
      setSchool(next);
      writeBhriguSchoolScope(next);
      return;
    }
    setSchool(readBhriguSchoolScope());
  }, [enabled, referrerSchool]);

  const updateSchool = useCallback((next) => {
    const normalized = normalizeBhriguSchoolScope(next);
    writeBhriguSchoolScope(normalized);
    setSchool(normalized);
  }, []);

  return [school, updateSchool];
}

export default BhriguSchoolScopeChips;
